import type { ActionTree } from 'vuex'

import type { SegmentState } from './types'
import type { RootState } from '@/store/modules/types'
import {
  filterObjectToQueryString,
  insertDisabledCustomFieldSettings,
} from '@/utils/helpers'
import {
  convertCurrencyFilterToMajorUnits,
  convertCurrencyFilterToMinorUnits,
} from '@/utils/filter/'
import { getEffectiveFilterEngine } from '@/utils/filterEngine'
import { clone } from '@/utils/helpers/'
import { getContext } from '@/utils/general'

export const segmentActions: ActionTree<SegmentState, RootState> = {
  async CREATE_SEGMENT(
    { commit, rootState },
    { name, filter, favourite = false, userDefined = true, version = 2 } = {},
  ) {
    const rootStateObject = JSON.parse(JSON.stringify(rootState))
    if (!rootStateObject?.authModule?.account) {
      return
    }
    const { promoterOid } = rootStateObject.authModule.account
    const url = `promoter/${promoterOid}/filter-group`
    const filterForApi = convertCurrencyFilterToMinorUnits(filter)
    const body = {
      name,
      filter: filterForApi,
      favourite,
      version,
      userDefined,
      promoterOid: promoterOid,
    }
    // Do not notify promoters anything change for scratch or internal filter
    let notVisibleToClient = false
    if (name === '_scratch_' || name === '_internal_') {
      notVisibleToClient = true
    }

    const { $axios, $arNotification } = getContext()

    try {
      if (!notVisibleToClient) {
        commit('SET_IS_CREATING_SEGMENT', true)
      }

      const { data } = await $axios.post(url, body)
      // Inbound: the /filter-group POST echoes the stored filter back in
      // cents (the default restful-resource behaviour, same as the GET on
      // this resource), so convert it back to dollars before committing —
      // mirrors the inbound conversion in the FETCH_SEGMENTS /
      // FETCH_SAVED_SEGMENTS_PAGINATED actions above.
      const dataWithMajorUnits = data?.filter
        ? { ...data, filter: convertCurrencyFilterToMajorUnits(data.filter) }
        : data
      if (name === '_internal_') {
        commit('SET_INTERNAL_SEGMENT', dataWithMajorUnits)
      }
      else if (name === '_scratch_') {
        commit('SET_SCRATCH_SEGMENT', dataWithMajorUnits)
      }
      else {
        commit('ADD_TO_SAVED_SEGMENT_LIST', dataWithMajorUnits)
        commit('SET_SCRATCH_SEGMENT_BY_SAVED_SEGMENT', dataWithMajorUnits.oid)
      }

      if (!notVisibleToClient) {
        $arNotification.push({
          type: 'success',
          message: 'Successfully created segment',
        })
      }
    }
    catch (e: any) {
      console.error(e)

      if (e.response && e.response.status === 409) {
        $arNotification.push({
          type: 'error',
          message: 'Did not duplicate segment, must have unique name',
        })
        console.error('Caught exception in CREATE_SEGMENT (409)', e)
        return
      }
      $arNotification.push({
        type: 'error',
        message: 'Failed to create segment',
      })
      console.error('Caught exception in CREATE_SEGMENT', e)
    }
    finally {
      commit('SET_IS_CREATING_SEGMENT', false)
    }
  },
  async DELETE_SEGMENT({ commit, rootState, state }, filter) {
    const rootStateObject = JSON.parse(JSON.stringify(rootState))
    if (!rootStateObject?.authModule?.account) {
      return
    }
    const { promoterOid } = rootStateObject.authModule.account

    const { $axios, $arNotification } = getContext()

    try {
      commit('SET_IS_DELETING_SEGMENT', true)

      const { data } = await $axios.get(
        `/promoter/${promoterOid}/message-list`,
        {
          params: {
            $skip: 0,
            $top: 1,
            $filter: `filterGroupOid=${filter.oid}`,
          },
        },
      )
      const relatedDynamicMessageList = data[0]

      await $axios.delete(
        `/promoter/${promoterOid}/filter-group/${filter.oid}`,
      )

      commit('DELETE_FROM_SAVED_SEGMENT_LIST', filter.oid)

      if (
        state.scratchSegmentInfo.source === 'saved-audience-filter'
        && filter.oid === state.scratchSegmentInfo.sourceOid
      ) {
        commit('RESET_SCRATCH_SEGMENT')
      }

      $arNotification.push({
        type: 'success',
        message: 'Successfully deleted segment',
      })
      return true
    }
    catch (error) {
      console.error(error)

      $arNotification.push({
        type: 'error',
        message: 'Failed to delete segment',
      })
      return false
    }
    finally {
      commit('SET_IS_DELETING_SEGMENT', false)
    }
  },
  async FETCH_SEGMENT_CRITERIA(
    { commit, rootState, rootGetters },
    isCustomFieldPageEnabled,
  ) {
    const rootStateObject = JSON.parse(JSON.stringify(rootState))
    if (!rootStateObject?.authModule?.account) {
      return
    }
    const { promoterOid } = rootStateObject.authModule.account
    if (rootStateObject?.segmentModule?.segmentCriteriaGroups.length !== 0)
      return

    const filterEngine = getEffectiveFilterEngine()
    const url = `promoter/${promoterOid}/filter-criteria?filterEngine=${filterEngine}`
    const { $axios } = getContext()

    try {
      const { data } = await $axios.get(url)

      const filterCriteriaData = insertDisabledCustomFieldSettings(
        isCustomFieldPageEnabled,
        clone(data),
      )

      // Update "Segment" title to "Saved Filters"
      // THIS IS TO BE REMOVED AS PART OF THIS TASK TO UPDATE THE SERVER-DERIVED WORDING
      filterCriteriaData.forEach((group: any) => {
        if (group.data) {
          group.data.forEach((criterion: any) => {
            if (
              criterion.resource === 'segment'
              && criterion.title === 'Segment'
            ) {
              criterion.title = 'Saved filters'
            }
          })
        }
      })

      commit('SET_SEGMENT_CRITERIA', filterCriteriaData)
    }
    catch (error) {
      console.error(error)
      console.error('Caught exception in FETCH_SEGMENT_CRITERIA', error)
    }
  },
  async FETCH_SEGMENTS(
    { commit, dispatch, rootState },
    {
      select = 'oid,name,meta,filter,userDefined,favourite,version',
      top = 'all',
      orderby = 'sysMtime desc',
      filterString = null,
      filters = {
        expressions: [
          {
            key: 'userDefined',
            operator: '=',
            value: true,
          },
        ],
        logicalOperators: [],
      },
    } = {},
  ) {
    const rootStateObject = JSON.parse(JSON.stringify(rootState))
    if (!rootStateObject?.authModule?.account) {
      return
    }
    const { promoterOid } = rootStateObject.authModule.account
    const url = `promoter/${promoterOid}/filter-group`

    const { $axios, $arNotification } = getContext()

    try {
      const { data } = await $axios.get(url, {
        params: {
          $top: top,
          $filter: filterString
            ? filterString
            : filterObjectToQueryString(filters),
          $select: select,
          $orderby: `favourite desc,${orderby}`,
        },
      })
      const savedFilters = data.filter((filter: Segment) => {
        return filter.name !== '_internal_' && filter.name !== '_scratch_'
      })
      // Inbound: convert each fetched saved segment's currency filter values
      // from cents back to dollars before committing to state — the single
      // inbound conversion layer, symmetric to the outbound conversion above.
      // Assumes every stored currency value is cents. Per EC-4300, existing
      // AM3-origin segments may still be stored in dollars with no
      // origin/unit marker, and EC-4300 explicitly rules out a x100
      // migration of those values ("units are unknowable... must not be
      // migrated"). Unconverted dollar values will show as a visibly-low
      // amount here, which in practice nudges the promoter to re-enter it —
      // a soft version of EC-4300's re-confirmation approach.
      const savedFiltersWithMajorUnits = savedFilters.map((segment: Segment) =>
        segment.filter
          ? { ...segment, filter: convertCurrencyFilterToMajorUnits(segment.filter) }
          : segment,
      )
      commit('SET_SAVED_SEGMENT_LIST', savedFiltersWithMajorUnits)
    }
    catch (error) {
      console.error(error)
      $arNotification.push({
        type: 'error',
        message: 'Failed to fetch segments',
      })
      console.error('Caught exception in FETCH_SEGMENTS', error)
    }
  },
  async FETCH_SAVED_SEGMENTS_PAGINATED(
    { commit, state, rootState },
    {
      top = 25,
      searchString = '',
      reload = false,
    }: { top?: number; searchString?: string; reload?: boolean } = {},
  ) {
    const rootStateObject = JSON.parse(JSON.stringify(rootState))
    if (!rootStateObject?.authModule?.account) {
      return
    }
    const { promoterOid } = rootStateObject.authModule.account
    const url = `promoter/${promoterOid}/filter-group`
    const { $axios } = getContext()

    const BASE_FILTER = 'userDefined = true AND name != "_internal_" AND name != "_scratch_"'
    const filterString = searchString
      ? `(${BASE_FILTER}) AND name ILIKE "%${searchString}%"`
      : BASE_FILTER

    const skip = reload ? 0 : state.savedSegmentsPaginated.length

    commit('SET_IS_FETCHING_SAVED_SEGMENTS', true)
    try {
      const { data } = await $axios.get(url, {
        params: {
          $count: true,
          $top: top,
          $skip: skip,
          $filter: filterString,
          $select: 'oid,name,meta,filter,userDefined,favourite,version',
          $orderby: 'favourite desc,sysMtime desc',
        },
      })

      // Inbound: convert each fetched saved segment's currency filter values
      // from cents back to dollars before committing to state / returning —
      // the single inbound conversion layer, symmetric to the outbound
      // conversion elsewhere in this module.
      const dataWithMajorUnits = {
        ...data,
        rows: (data?.rows || []).map((segment: Segment) =>
          segment.filter
            ? { ...segment, filter: convertCurrencyFilterToMajorUnits(segment.filter) }
            : segment,
        ),
      }

      if (reload) {
        commit('SET_SAVED_SEGMENTS_PAGINATED', dataWithMajorUnits)
      }
      else {
        commit('CONCAT_SAVED_SEGMENTS_PAGINATED', dataWithMajorUnits)
      }
      commit('SET_SAVED_SEGMENTS_COUNT', dataWithMajorUnits)
      commit('SET_IS_NO_MORE_SAVED_SEGMENTS', dataWithMajorUnits)

      return dataWithMajorUnits
    }
    catch (error) {
      console.error(error)
      console.error('Caught exception in FETCH_SAVED_SEGMENTS_PAGINATED', error)
    }
    finally {
      commit('SET_IS_FETCHING_SAVED_SEGMENTS', false)
    }
  },
  async UPDATE_SAVED_SEGMENT(
    { commit, rootState, state },
    { oid, editFilter },
  ) {
    const rootStateObject = JSON.parse(JSON.stringify(rootState))
    if (!rootStateObject?.authModule?.account) {
      return
    }
    const { promoterOid } = rootStateObject.authModule.account

    const { $axios, $arNotification } = getContext()

    const url = `promoter/${promoterOid}/filter-group/${oid}`
    try {
      commit('SET_IS_PATCHING_SEGMENT', true)
      const editFilterForApi = editFilter?.filter
        ? {
            ...editFilter,
            filter: convertCurrencyFilterToMinorUnits(editFilter.filter),
          }
        : editFilter
      await $axios.patch(url, editFilterForApi)
      commit('PATCH_SEGMENT_IN_SAVED_SEGMENT_LIST', { oid, editFilter })

      // If source of scratch filter is also the filter you're gonna patch
      if (
        state.scratchSegmentInfo.source === 'saved-audience-filter'
        && oid === state.scratchSegmentInfo.sourceOid
      ) {
        if (editFilter.filter) {
          // If filter field exists, set entire scratch filter by the filter you just patched
          commit('SET_SCRATCH_SEGMENT_BY_SAVED_SEGMENT', oid)
        }
        else {
          // If it doesn't include filter field, just patch fields that were updated
          commit('PATCH_SCRATCH_SEGMENT', editFilter)
        }
      }

      $arNotification.push({
        type: 'success',
        message: 'Filter updated successfully',
      })
      return true
    }
    catch (error: any) {
      console.error(error)

      if (error.response && error.response.status === 409) {
        $arNotification.push({
          type: 'error',
          message: 'Did not duplicate segment, must have unique name',
        })
      }
      else {
        $arNotification.push({
          type: 'error',
          message: 'Failed to update segment',
        })
      }
      console.error('Caught exception in UPDATE_SAVED_SEGMENT', error)
      return false
    }
    finally {
      commit('SET_IS_PATCHING_SEGMENT', false)
    }
  },
  async UPDATE_SCRATCH_SEGMENT(
    { commit, rootState, state, getters, rootGetters },
    { route }: any,
  ) {
    const rootStateObject = JSON.parse(JSON.stringify(rootState))
    if (!rootStateObject?.authModule?.account) {
      return
    }
    if (!state.scratchSegment) {
      return
    }
    const { promoterOid } = rootStateObject.authModule.account

    if (state.scratchSegmentInfo.source === 'message-segment') {
      const prunedScratchSegmentFilter = clone(
        getters.prunedScratchSegment.filter,
      )
      let targetingFilter = clone(
        rootGetters['messageListModule/getCurrentFilterExpression'],
      )

      if (!targetingFilter) {
        targetingFilter = prunedScratchSegmentFilter
      }
      else {
        if (!targetingFilter.conditions) {
          targetingFilter.conditions = []
        }
        if (!targetingFilter.logic) {
          targetingFilter.logic = []
        }

        if (
          !!prunedScratchSegmentFilter.conditions
          && prunedScratchSegmentFilter.conditions.length > 0
        ) {
          targetingFilter.conditions
            = prunedScratchSegmentFilter.conditions.concat(
              targetingFilter.conditions,
            )

          if (prunedScratchSegmentFilter.logic.length > 0) {
            targetingFilter.logic = ['(']
              .concat(prunedScratchSegmentFilter.logic)
              .concat([')'])
              .concat(['and'])
              .concat(targetingFilter.logic)
          }
          else {
            targetingFilter.logic = ['and'].concat(targetingFilter.logic)
          }
        }
      }

      const routeParts = route.path.split('/')
      const mainPage = routeParts[1]

      if (
        !!targetingFilter
        && mainPage == 'message-center'
        && routeParts.length >= 3
      ) {
        const messageType = routeParts[3]

        if (messageType === 'email') {
          commit(
            'messageModule/PUT_FILTERING_IN_SCRATCH_EMAIL_MESSAGE',
            targetingFilter,
            { root: true },
          )
        }
        else {
          commit(
            'messageModule/PUT_FILTERING_IN_SCRATCH_SIMPLE_MESSAGE',
            targetingFilter,
            { root: true },
          )
        }
      }
    }
    else {
      const url = `promoter/${promoterOid}/filter-group/${state.scratchSegment.oid}`
      const body = {
        filter: convertCurrencyFilterToMinorUnits(
          getters.prunedScratchSegment.filter,
        ),
      }

      const { $axios } = getContext()

      try {
        await $axios.patch(url, body)
      }
      catch (error) {
        console.error(error)
        console.error('Caught exception in UPDATE_SCRATCH_SEGMENT', error)
      }
    }
  },
}
